/**
 * @fileoverview Deactivate User form hook.
 * @description The Deactivate dialog's form logic: the reason and note fields, validated by the
 * schema with the configuration's note limit, the translated field errors, and the submit handler
 * that turns the validated values into the request body (the note is left out when blank).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { OTHER_REASON_CODE } from '@/features/users/constants/lifecycle.constants'
import {
  buildDeactivateUserFormSchema,
  type DeactivateUserFormValues,
} from '@/features/users/schemas/deactivate-user-form.schema'

import type { DeactivateUserRequest } from '@/features/users/types/users.types'
import type { FormEvent } from 'react'

/**
 * @description Builds the Deactivate User body from the form: the note is left out when blank.
 * @param {DeactivateUserFormValues} values - The validated values.
 * @returns {DeactivateUserRequest} The request body.
 */
export function toDeactivateUserRequest(values: DeactivateUserFormValues): DeactivateUserRequest {
  return {
    reasonCode: values.reasonCode,
    ...(values.reasonNote !== '' && { reasonNote: values.reasonNote }),
  }
}

/**
 * @description Runs the Deactivate form: its fields, validation and submit.
 * @param {number} noteMaxLength - The longest the note may be.
 * @param {(payload: DeactivateUserRequest) => void} submitReason - What to do with the request once
 * the form is valid.
 * @returns {object} The form, whether the note is required, the translated field errors, and the
 * submit handler for the form element.
 */
export function useDeactivateUserForm(
  noteMaxLength: number,
  submitReason: (payload: DeactivateUserRequest) => void,
) {
  const { t } = useTranslation()
  const form = useForm<DeactivateUserFormValues>({
    resolver: zodResolver(buildDeactivateUserFormSchema({ noteMaxLength })),
    defaultValues: { reasonCode: '', reasonNote: '' },
  })
  const { errors } = form.formState
  const reasonCode = useWatch({ control: form.control, name: 'reasonCode' })

  /**
   * @description Translates a validation message key, if there is one.
   * @param {string | undefined} key - The i18n key from the schema, if the field is invalid.
   * @returns {string | undefined} The message to show.
   */
  const message = (key: string | undefined) => (key ? t(key) : undefined)

  /**
   * @description Sends the chosen reason.
   * @param {DeactivateUserFormValues} values - The validated values.
   */
  const onValid = (values: DeactivateUserFormValues) => {
    submitReason(toDeactivateUserRequest(values))
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void form.handleSubmit(onValid)(event)
  }

  return {
    form,
    noteRequired: reasonCode === OTHER_REASON_CODE,
    reasonError: message(errors.reasonCode?.message),
    noteError: message(errors.reasonNote?.message),
    onSubmit,
  }
}
